import { useMemo } from 'react';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { PresaveCampaignStatus } from 'fym-shared/src/smartlinks/presave';
import { PublishedPageKind } from '../published/publishedPageKind';
import { smartLinkPageKind } from './smartLinkPageKind';
import { usePresaveCampaign } from '../presave/usePresaveCampaign';
import { useFetchSmartLinkStatsQuery } from '../../../redux/api/fymAPI';

export interface SmartLinkPageFacts {
  kind: PublishedPageKind;
  /** The page is reachable by anyone: a version of it is published. */
  isPublished: boolean;
  /** Page views over the window the stats endpoint answers for. Null while unknown. */
  viewCount: number | null;
  /** The window those views cover, in days. */
  viewDays: number;
  /** Sign-ups the campaign still owes fans. Null when there is no live campaign. */
  pendingPresaveCount: number | null;
  /** Addresses this page collected. Null when there is no campaign. */
  emailCount: number | null;
  /** The campaign's release date, on a page that still counts down to one. */
  releaseDate: string | null;
  /** The campaign behind the page, when it has one. What the export is taken from. */
  campaignUuid: string | null;
  /** The track the campaign is for, so a download can be named after it. */
  trackTitle: string | null;
  /** True until every number above is settled, so nothing is drawn from a gap. */
  isLoading: boolean;
}

/** The stats endpoint answers for a fixed fortnight; the copy must say so. */
export const STATS_WINDOW_DAYS = 14;

/**
 * What the manage screen knows about the page, in numbers.
 *
 * Every figure comes from an endpoint the screen already reads — the stats
 * summary and the pre-save campaign — so the header and the delete dialog
 * quote the same numbers as the panels below them. A figure that has not
 * arrived stays null, and whoever draws it drops the row instead of guessing.
 */
export function useSmartLinkPageFacts(accountId: number, smartLink: SerializedSmartLink): SmartLinkPageFacts {
  const {
    hasPresaveBlock,
    campaign,
    campaignUuid,
    stats,
    isLoading: isCampaignLoading,
  } = usePresaveCampaign({
    accountId,
    smartLink,
  });
  const { data: statsResponse, isLoading: isStatsLoading } = useFetchSmartLinkStatsQuery({
    accountId,
    smartLinkId: smartLink.id,
  });

  const pendingPresaveCount = useMemo(() => {
    // Only a scheduled campaign owes anybody anything: a released one has
    // already delivered, and a cancelled one stopped taking sign-ups.
    if (!stats || campaign?.status !== PresaveCampaignStatus.scheduled) {
      return null;
    }
    return stats.byService.reduce((sum, row) => sum + row.count, 0) + stats.emailCount;
  }, [campaign?.status, stats]);

  return {
    kind: smartLinkPageKind(smartLink),
    isPublished: smartLink.publishedSmartLinkVersion !== null,
    viewCount: statsResponse?.stats.totals.views ?? null,
    viewDays: STATS_WINDOW_DAYS,
    pendingPresaveCount,
    emailCount: stats?.emailCount ?? null,
    releaseDate: campaign?.status === PresaveCampaignStatus.scheduled ? campaign.releaseDate : null,
    campaignUuid: campaignUuid ?? null,
    trackTitle: campaign?.trackTitle ?? null,
    isLoading: isStatsLoading || (hasPresaveBlock && isCampaignLoading),
  };
}
